import { memo, useMemo, useState } from 'react'

import { AddLink, LinkOff } from '@mui/icons-material'
import { Button, IconButton, Paper, Stack, Switch, Tooltip, Typography } from '@mui/material'
import { AvcTaskAssociation } from '@nativewaves/nw-cloud-sdk/content'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import {
  AssociationRow,
  TaskPickerDialogContent,
  useAvcTaskAssociationRows,
} from 'domains/Workspaces/Streams/AvcTaskAssociations'

/**
 * The only thing this section needs from the surrounding form — kept minimal so it does not
 * depend on the General form's full value type.
 */
type AvcTaskAssociationsFormValues = {
  avcTaskAssociations: AvcTaskAssociation[]
}

export type EditableSectionProps = {
  /** Scopes the task picker to the stream's own repository. */
  repositoryId?: string
}

const EditableSectionRaw = ({ repositoryId }: EditableSectionProps) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])

  const [linkDialogOpen, setLinkDialogOpen] = useState(false)

  const { control } = useFormContext<AvcTaskAssociationsFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: 'avcTaskAssociations' })

  const rows = useAvcTaskAssociationRows(fields)

  const linkedTaskIds = useMemo(() => new Set(fields.map((field) => field.taskId)), [fields])

  return (
    <Paper sx={{ px: 4, py: 3 }} elevation={0}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="heading-md">{t('entity:content.avcTask', { count: 2 })}</Typography>
        <Button
          sx={{ ml: 'auto' }}
          size="small"
          variant="outlined"
          endIcon={<AddLink />}
          onClick={() => setLinkDialogOpen(true)}
        >
          {t('domain:Workspace.AvcTaskAssociations.link')}
        </Button>
      </Stack>

      {rows.length ? (
        <Stack sx={{ rowGap: 1 }}>
          {rows.map((row, index) => (
            <AssociationRow
              key={fields[index].id}
              row={row}
              EnabledSlot={
                <Controller
                  name={`avcTaskAssociations.${index}.enabled`}
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      size="small"
                      checked={field.value}
                      inputProps={{ 'aria-label': t('domain:Workspace.AvcTaskAssociations.enabled') }}
                    />
                  )}
                />
              }
              ActionsSlot={
                <Tooltip title={t('domain:Workspace.AvcTaskAssociations.unlink')} placement="left">
                  <IconButton size="small" onClick={() => remove(index)}>
                    <LinkOff fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              }
            />
          ))}
        </Stack>
      ) : (
        <Typography sx={{ py: 2, display: 'block', color: 'text.secondary' }} variant="text-sm">
          {t('domain:Workspace.AvcTaskAssociations.none')}
        </Typography>
      )}

      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        title={t('domain:Workspace.AvcTaskAssociations.selectTaskTitle')}
      >
        <TaskPickerDialogContent
          repositoryId={repositoryId}
          linkedTaskIds={linkedTaskIds}
          // Linked enabled — the row's switch is where it gets turned off again.
          onSelect={(avcTask) => append({ taskId: avcTask.id, enabled: true })}
        />
      </Dialog>
    </Paper>
  )
}

export const EditableSection = memo(EditableSectionRaw)
